import { useEffect, useMemo, useRef, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import type { FileRejection } from 'react-dropzone';
import ReactPlayer from 'react-player';

import { propertyAdEditorProvider } from '../../providers/propertyAdEditorProvider';
import type { AdMediaType } from '../../types/ads';
import { FileKindEnum } from '../../types/enums';
import type { PropertyAdInProgressWorkspace } from '../../types/propertyAdEditor';

import { FileUploader } from '../uiComponents/FileUploader';
import { EditorSection } from './EditorSection';

export const PROPERTY_AD_VIDEO_LIMIT = 3;
export const PROPERTY_AD_VIDEO_MAX_SIZE_BYTES = 300 * 1024 * 1024;
export const PROPERTY_AD_VIDEO_ACCEPT = { 'video/mp4': ['.mp4'] };
export const PROPERTY_AD_VIDEO_FILE_TYPES = ['video/mp4', 'video/quicktime'];

interface GalleryVideo {
  fileId: string;
  order: number;
  url: string;
}

interface UploadVideoSectionProps {
  disabled?: boolean;
  onMediaChanged?: (media: AdMediaType[]) => void;
  onUploadComplete?: (ok: boolean) => void;
  onUploadStart?: () => void;
  persistVideoMedia?: (
    videos: AdMediaType[]
  ) => ReturnType<typeof propertyAdEditorProvider.updateInProgressMedia>;
  workspace: Pick<PropertyAdInProgressWorkspace, 'id' | 'propertyAdMedia'>;
}

const getWorkspaceVideos = (media: AdMediaType[] | null | undefined): GalleryVideo[] =>
  (media ?? [])
    .filter(
      (item): item is AdMediaType & { fileId: string; fileUrl: string } =>
        item.kind === FileKindEnum.Video && Boolean(item.fileId) && Boolean(item.fileUrl)
    )
    .sort((first, second) => first.order - second.order)
    .map(({ fileId, fileUrl }, index) => ({ fileId, order: index + 1, url: fileUrl }));

const getMediaUpdate = (media: AdMediaType[] | null | undefined, videos: GalleryVideo[]) => [
  ...(media ?? [])
    .filter((item) => item.kind !== FileKindEnum.Video)
    .map(({ fileId, kind, mediaType, order }) => ({ fileId, kind, mediaType, order })),
  ...videos.map(({ fileId }, index) => ({
    fileId,
    kind: FileKindEnum.Video,
    order: index + 1,
  })),
];

const getVideoMedia = (videos: GalleryVideo[]): AdMediaType[] =>
  videos.map(({ fileId, url }, index) => ({
    fileId,
    fileUrl: url,
    kind: FileKindEnum.Video,
    order: index + 1,
  }));

const getConfirmedVideos = (
  responseMedia: AdMediaType[] | null | undefined,
  requestedVideos: GalleryVideo[]
): GalleryVideo[] => {
  if (!Array.isArray(responseMedia)) return requestedVideos;

  const requestedVideoById = new Map(
    requestedVideos.map((video) => [video.fileId, video] as const)
  );
  const confirmedVideos = responseMedia
    .filter(
      (item): item is AdMediaType & { fileId: string } =>
        item.kind === FileKindEnum.Video && Boolean(item.fileId)
    )
    .sort((first, second) => first.order - second.order)
    .map((item, index) => ({
      fileId: item.fileId,
      order: index + 1,
      url: item.fileUrl ?? requestedVideoById.get(item.fileId)?.url ?? '',
    }));

  return confirmedVideos.every(({ url }) => Boolean(url)) ? confirmedVideos : requestedVideos;
};

export const UploadVideoSection = ({
  disabled = false,
  onMediaChanged,
  onUploadComplete,
  onUploadStart,
  persistVideoMedia,
  workspace,
}: UploadVideoSectionProps) => {
  const [videos, setVideos] = useState<GalleryVideo[]>(() =>
    getWorkspaceVideos(workspace.propertyAdMedia)
  );
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const videosRef = useRef(videos);
  const otherMedia = useMemo(
    () => (workspace.propertyAdMedia ?? []).filter((item) => item.kind !== FileKindEnum.Video),
    [workspace.propertyAdMedia]
  );

  useEffect(() => {
    const hydratedVideos = getWorkspaceVideos(workspace.propertyAdMedia);
    videosRef.current = hydratedVideos;
    setVideos(hydratedVideos);
    setError('');
  }, [workspace.id, workspace.propertyAdMedia]);

  const persistVideos = async (nextVideos: GalleryVideo[]) => {
    setError('');
    setIsSaving(true);

    try {
      const response = persistVideoMedia
        ? await persistVideoMedia(getVideoMedia(nextVideos))
        : await propertyAdEditorProvider.updateInProgressMedia(workspace.id, {
            propertyAdMedia: getMediaUpdate(workspace.propertyAdMedia, nextVideos),
          });

      if (!response.ok) {
        setError(response.message || "Couldn't save video changes. Please try again.");
        return false;
      }

      const confirmedVideos = getConfirmedVideos(response.data?.propertyAdMedia, nextVideos).map(
        (video, index) => ({ ...video, order: index + 1 })
      );
      videosRef.current = confirmedVideos;
      setVideos(confirmedVideos);
      if (!persistVideoMedia) {
        onMediaChanged?.([
          ...otherMedia,
          ...confirmedVideos.map(({ fileId, order, url }) => ({
            fileId,
            fileUrl: url,
            kind: FileKindEnum.Video,
            order,
          })),
        ]);
      }
      return true;
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Couldn't save video changes. Please try again."
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleVideoUploaded = async (fileId: string, url: string) => {
    return persistVideos([
      ...videosRef.current,
      { fileId, order: videosRef.current.length + 1, url },
    ]);
  };

  const handleDelete = (fileId: string) => {
    void persistVideos(videosRef.current.filter((video) => video.fileId !== fileId));
  };

  const handleRejectedVideos = (rejections: FileRejection[]) => {
    const hasOversizedVideo = rejections.some(({ errors }) =>
      errors.some(({ code }) => code === 'file-too-large')
    );
    setError(
      hasOversizedVideo ? 'Each video must be 300 MB or smaller.' : 'Only MPEG4 videos are allowed.'
    );
  };

  return (
    <EditorSection
      cardTestId='upload-video-card'
      description='Add videos to showcase the property'
      title='Upload video'
      titleId='upload-video-title'
    >
      <Stack minWidth={0} spacing={3}>
        {videos.length > 0 && (
          <Box
            aria-label='Listing videos'
            component='section'
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              minWidth: 0,
            }}
          >
            {videos.map((video) => (
              <Box
                aria-label={`Video ${video.order}`}
                component='article'
                key={video.fileId}
                sx={{
                  backgroundColor: '#000',
                  border: '1px solid #dce9f7',
                  borderRadius: 2,
                  minWidth: 0,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box sx={{ aspectRatio: '315 / 190', minWidth: 0, width: '100%' }}>
                  <ReactPlayer controls height='100%' url={video.url} width='100%' />
                </Box>
                <IconButton
                  aria-label={`Delete video ${video.order}`}
                  disabled={disabled || isSaving}
                  onClick={() => handleDelete(video.fileId)}
                  size='small'
                  sx={{
                    backgroundColor: '#fff',
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    '&:hover': { backgroundColor: '#f7fafc' },
                  }}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <FileUploader
          accept={PROPERTY_AD_VIDEO_ACCEPT}
          awaitOnUploaded
          disabled={disabled || isSaving || videos.length >= PROPERTY_AD_VIDEO_LIMIT}
          fileTypes={PROPERTY_AD_VIDEO_FILE_TYPES}
          fullWidth
          labelButton='Upload video'
          labelFormat='Only MPEG4 format'
          labelSize='Maximum 300 MB'
          limit={PROPERTY_AD_VIDEO_LIMIT - videos.length}
          maxSize={PROPERTY_AD_VIDEO_MAX_SIZE_BYTES}
          onRejected={handleRejectedVideos}
          onUploadComplete={onUploadComplete}
          onUploadStart={onUploadStart}
          onUploaded={handleVideoUploaded}
          type='video'
        />

        <Stack
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent='space-between'
          spacing={1}
        >
          <Typography color='#6f7d87' component='p' variant='body2'>
            {videos.length} of {PROPERTY_AD_VIDEO_LIMIT} videos
          </Typography>
          {isSaving && (
            <Typography
              aria-live='polite'
              color='#6f7d87'
              component='p'
              role='status'
              variant='body2'
            >
              Saving video changes...
            </Typography>
          )}
        </Stack>
        {error && (
          <Typography color='error' component='p' role='alert' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </EditorSection>
  );
};
